import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ArrowLeft, Calendar, FileText, Clock, User, Plus, CalendarPlus, Settings, CalendarCheck, Trash2, Edit } from 'lucide-react';

import { useEmployees } from '../../hooks/useEmployees';
import { useVacationRequests } from '../../hooks/useVacationRequests';
import { VoucherModal } from './VoucherModal';
import { VacationRequestDialog } from './VacationRequestDialog';
import { AdministrativeRequestDialog } from './AdministrativeRequestDialog';
import { AddDaysDialog } from './AddDaysDialog';
import { ProgressiveDaysDialog } from './ProgressiveDaysDialog';
import { EditEmployeeDialog } from './EditEmployeeDialog';
import { formatChileanDate } from '../../utils/dateUtils';
import { EditVacationRequestDialog } from './EditVacationRequestDialog';

export const EmployeeDetailView = ({ employeeId, onBack, onRefreshEmployees }) => {
  const { employees, loading: employeesLoading, addDaysToEmployee, loadEmployees, updateEmployee } = useEmployees();
  const { requests, loading: requestsLoading, getRequestsByEmployee, deleteRequest } = useVacationRequests();
  
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [vacationRequestOpen, setVacationRequestOpen] = useState(false);
  const [administrativeRequestOpen, setAdministrativeRequestOpen] = useState(false);
  const [addDaysOpen, setAddDaysOpen] = useState(false);
  const [addDaysType, setAddDaysType] = useState('progressive');
  const [progressiveDaysOpen, setProgressiveDaysOpen] = useState(false);
  const [editEmployeeOpen, setEditEmployeeOpen] = useState(false);
  const [employeeRequests, setEmployeeRequests] = useState([]);
  const [editRequestOpen, setEditRequestOpen] = useState(false);
  const [requestToEdit, setRequestToEdit] = useState(null);

  const employee = employees.find(emp => emp.id === employeeId);

  // Cargar solicitudes del empleado
  useEffect(() => {
    if (employeeId) {
      const loadEmployeeRequests = async () => {
        const requests = await getRequestsByEmployee(employeeId);
        console.log('Solicitudes cargadas para empleado:', employeeId, requests);
        setEmployeeRequests(requests);
      };
      loadEmployeeRequests();
    }
  }, [employeeId]); // Removed getRequestsByEmployee from dependencies to prevent loops

  if (employeesLoading) {
    return (
      <div className="container mx-auto p-6">
        <Button variant="outline" onClick={onBack} className="mb-4">
          <ArrowLeft size={16} className="mr-2" />
          Volver
        </Button>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Cargando empleado...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="container mx-auto p-6">
        <Button variant="outline" onClick={onBack} className="mb-4">
          <ArrowLeft size={16} className="mr-2" />
          Volver
        </Button>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Empleado no encontrado</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleShowVoucher = (request) => {
    setSelectedRequest(request);
    setVoucherModalOpen(true);
  };

  const handleAddDays = async (employeeId, type, days, reason) => {
    console.log('EmployeeDetailView: handleAddDays llamado con:', { employeeId, type, days, reason });
    try {
      await addDaysToEmployee(employeeId, type, days, reason);
      setAddDaysOpen(false);
    } catch (error) {
      // El error ya se maneja en el hook
    }
  };

  const handleUpdateEmployee = async (employeeId, updates) => {
    try {
      await updateEmployee(employeeId, updates);
      await loadEmployees(); // Recargar empleados para reflejar cambios
      return true;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  };

  const handleRequestCreated = async (request) => {
    setSelectedRequest(request);
    setVoucherModalOpen(true);
    // Recargar solicitudes del empleado y datos del empleado
    getRequestsByEmployee(employeeId).then(setEmployeeRequests);
    // Recargar empleados para actualizar días disponibles
  await loadEmployees();
  // Notificar al dashboard para refrescar la grilla si está disponible
  try { await onRefreshEmployees?.(); } catch {}
  };

  const handleDeleteRequest = async (requestId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta solicitud?')) {
      try {
        await deleteRequest(requestId);
        // Recargar solicitudes del empleado y datos del empleado
        const updatedRequests = await getRequestsByEmployee(employeeId);
        setEmployeeRequests(updatedRequests);
  // Recargar empleados y notificar dashboard para actualizar días disponibles
  await loadEmployees();
  try { await onRefreshEmployees?.(); } catch {}
      } catch (error) {
        console.error('Error al eliminar solicitud:', error);
      }
    }
  };

  const handleEditRequest = (request) => {
    setRequestToEdit(request);
    setEditRequestOpen(true);
  };

  const handleEditSuccess = async () => {
    // Recargar solicitudes del empleado y datos del empleado
    const updatedRequests = await getRequestsByEmployee(employeeId);
    setEmployeeRequests(updatedRequests);
  await loadEmployees();
  try { await onRefreshEmployees?.(); } catch {}
  };

  const availableDays = employee.vacation_days_available ?? employee.vacationDaysAvailable ?? 0;
  const availableAdminDays = employee.admin_days_available ?? employee.adminDaysAvailable ?? 0;
  const progressiveCurrentPeriod = employee.progressive_days_current_period ?? employee.progressiveDaysCurrentPeriod ?? 0;
  const progressiveTotalsSinceApproval = employee.progressive_days_total_since_approval ?? employee.progressiveDaysTotalSinceApproval ?? 0;

  const getAvailableStatusColor = () => {
    if (availableDays <= 0) return '#9CA3AF'; // text-gray-400
    if (availableDays <= 5) return '#F59E0B'; // text-amber-500/orange-500
    return '#10B981'; // text-emerald-500/success
  };
  const years = Math.floor((Date.now() - new Date(employee.start_date).getTime()) / (365.25 * 24 * 60 * 60 * 1000));

  const approvedRequests = Array.isArray(employeeRequests) ? employeeRequests.filter(req => req.status === 'approved') : [];
  const pendingRequests = Array.isArray(employeeRequests) ? employeeRequests.filter(req => req.status === 'pending') : [];
  const rejectedRequests = Array.isArray(employeeRequests) ? employeeRequests.filter(req => req.status === 'rejected') : [];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft size={16} className="mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <User size={24} className="text-primary" />
                Informe Detallado - {employee.name}
              </h1>
              <p className="text-muted-foreground">{employee.position}</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setEditEmployeeOpen(true)}>
            <Edit size={16} className="mr-2" />
            Editar Empleado
          </Button>
        </div>
      </div>

      {/* Panel de Acciones */}
      <div style={{
        backgroundColor: '#F8FAFC',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        padding: '24px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          marginBottom: '20px',
          color: '#1E293B'
        }}>
          Acciones Disponibles
        </h3>
        
        {/* Solicitudes */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Solicitudes
          </h4>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            maxWidth: '100%'
          }}>
            <Button 
              onClick={() => setVacationRequestOpen(true)}
              style={{
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                minWidth: '180px',
                maxWidth: '220px',
                flex: '0 0 auto'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#10B981'}
            >
              <CalendarCheck size={18} />
              Solicitar Vacaciones
            </Button>

            <Button 
              onClick={() => setAdministrativeRequestOpen(true)}
              style={{
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                minWidth: '180px',
                maxWidth: '220px',
                flex: '0 0 auto'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563EB'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3B82F6'}
            >
              <CalendarCheck size={18} />
              Solicitar Administrativo
            </Button>
          </div>
        </div>

        {/* Gestión de Días */}
        <div>
          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Gestión de Días
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            <Button 
              onClick={() => setProgressiveDaysOpen(true)}
              style={{
                backgroundColor: '#F59E0B',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#D97706'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#F59E0B'}
            >
              <Plus size={16} />
              Días Progresivos
            </Button>
            
            <Button 
              onClick={() => {
                setAddDaysType('additional');
                setAddDaysOpen(true);
              }}
              style={{
                backgroundColor: '#8B5CF6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#7C3AED'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#8B5CF6'}
            >
              <CalendarPlus size={16} />
              Días Adicionales
            </Button>

            <Button 
              onClick={() => {
                setAddDaysType('administrative');
                setAddDaysOpen(true);
              }}
              style={{
                backgroundColor: '#1E40AF',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1D4ED8'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#1E40AF'}
            >
              <Settings size={16} />
              Días Administrativos
            </Button>

            <Button 
              onClick={() => {
                setAddDaysType('used');
                setAddDaysOpen(true);
              }}
              style={{
                backgroundColor: '#DC2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#B91C1C'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#DC2626'}
            >
              <Settings size={16} />
              Días Ya Utilizados
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información del Empleado */}
        <div className="lg:col-span-1 space-y-6">
          {/* Resumen de Días */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Resumen de Días</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-4 bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg">
                <div className="text-3xl font-bold" style={{ color: getAvailableStatusColor() }}>
                  {availableDays.toFixed(2)}
                </div>
                <p className="text-sm font-medium text-muted-foreground">
                  Días Vacaciones Disponibles
                </p>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-r from-accent/5 to-secondary/5 rounded-lg">
                <div className="text-3xl font-bold" style={{ color: '#3B82F6' }}>
                  {availableAdminDays}
                </div>
                <p className="text-sm font-medium text-muted-foreground">
                  Días Administrativos Disponibles
                </p>
              </div>

              <Separator />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Días Legales Acumulados:</span>
                  <span className="font-medium">{(employee.accumulated_days || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Días Progresivos (período):</span>
                  <span className="font-medium">{progressiveCurrentPeriod}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Días Progresivos (totales aplicados):</span>
                  <span className="font-medium">{progressiveTotalsSinceApproval}</span>
                </div>
                {employee.additional_days && employee.additional_days > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Días Adicionales:</span>
                    <span className="font-medium">{employee.additional_days}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Días Utilizados:</span>
                  <span className="font-medium text-red-600">-{(employee.used_days || 0).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Información Personal */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Información Personal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fecha de Ingreso:</span>
                <span className="font-medium">{formatChileanDate(employee.start_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Antigüedad:</span>
                <span className="font-medium">{years} {years === 1 ? 'año' : 'años'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">RUT:</span>
                <span className="font-medium">{employee.rut || 'No especificado'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Días Legales Anuales:</span>
                <span className="font-medium">{employee.legal_days}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Historial de Solicitudes */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar size={20} />
                Historial de Solicitudes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!Array.isArray(employeeRequests) || employeeRequests.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No hay solicitudes de vacaciones</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Estadísticas */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-3 bg-success/10 rounded-lg">
                      <div className="text-xl font-bold text-success">{approvedRequests.length}</div>
                      <p className="text-xs text-muted-foreground">Aprobadas</p>
                    </div>
                    <div className="text-center p-3 bg-orange-500/10 rounded-lg">
                      <div className="text-xl font-bold text-orange-500">{pendingRequests.length}</div>
                      <p className="text-xs text-muted-foreground">Pendientes</p>
                    </div>
                    <div className="text-center p-3 bg-destructive/10 rounded-lg">
                      <div className="text-xl font-bold text-destructive">{rejectedRequests.length}</div>
                      <p className="text-xs text-muted-foreground">Rechazadas</p>
                    </div>
                  </div>

                  {/* Lista de Solicitudes */}
                  <div className="space-y-3">
                    {Array.isArray(employeeRequests) && employeeRequests
                      .sort((a, b) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime())
                      .map((request) => (
                        <div key={request._id || request.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex items-center gap-2">
                                <Clock size={14} className="text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  {formatChileanDate(request.startDate)} - {' '}
                                  {formatChileanDate(request.endDate)}
                                </span>
                              </div>
                              <Badge variant={
                                request.status === 'pending' ? 'outline' : 
                                request.status === 'approved' ? 'default' : 'destructive'
                              }>
                                {request.status === 'pending' ? 'Pendiente' :
                                 request.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                              <div>
                                <span className="font-medium">Total: </span>
                                {request.totalDays || request.total_days} días
                              </div>
                              <div>
                                <span className="font-medium">Tipo: </span>
                                {request.type === 'full' ? 'Día completo' : 
                                 request.type === 'half' ? 'Medio día' : 'Administrativo'}
                              </div>
                            </div>
                            {request.notes && (
                              <p className="text-sm text-muted-foreground mt-2 italic">
                                "{request.notes}"
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleShowVoucher(request)}
                            >
                              <FileText size={14} className="mr-1" />
                              Comprobante
                            </Button>
                            <Button 
                              variant="secondary" 
                              size="sm"
                              onClick={() => handleEditRequest(request)}
                            >
                              <Edit size={14} className="mr-1" />
                              Editar
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => handleDeleteRequest(request._id || request.id)}
                              className="hover:bg-red-700"
                            >
                              <Trash2 size={14} className="mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modals y Diálogos */}
      <VoucherModal 
        request={selectedRequest}
        open={voucherModalOpen}
        onOpenChange={setVoucherModalOpen}
      />
      
      <VacationRequestDialog 
        employee={employee}
        open={vacationRequestOpen}
        onOpenChange={setVacationRequestOpen}
        onRequestCreated={handleRequestCreated}
      />

      <AdministrativeRequestDialog 
        employee={employee}
        open={administrativeRequestOpen}
        onOpenChange={setAdministrativeRequestOpen}
        onRequestCreated={handleRequestCreated}
      />
      
      <AddDaysDialog
        employee={employee}
        type={addDaysType}
        open={addDaysOpen}
        onOpenChange={setAddDaysOpen}
        onAddDays={handleAddDays}
      />

      <ProgressiveDaysDialog
        employee={employee}
        open={progressiveDaysOpen}
        onOpenChange={setProgressiveDaysOpen}
        onUpdateEmployee={handleUpdateEmployee}
      />

      <EditEmployeeDialog
        employee={employee}
        open={editEmployeeOpen}
        onOpenChange={setEditEmployeeOpen}
        onEmployeeUpdated={handleUpdateEmployee}
      />

      <EditVacationRequestDialog
        request={requestToEdit}
        open={editRequestOpen}
        onOpenChange={setEditRequestOpen}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};
